import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, LogOut, Menu, Search, ShoppingCart, User } from 'lucide-react';
import { AppContext } from 'context/AppContext';
import { customerMenuData } from './CustomerMenuData';
import { appName } from 'constants/constants';
import logoDark from 'assets/images/logo-dark.png';
import logoLight from 'assets/images/logo-light.png';
import userProfile from 'assets/images/users/user-profile.png';
import { Dropdown } from 'Common/Components/Dropdown';
import LightDark from 'Common/LightDark';

const CustomerHeader = () => {
  const { user } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/customer/products?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
    window.location.reload();
  };

  return (
    <header className="sticky top-0 z-[1000] w-full border-b border-slate-200 bg-white shadow-sm dark:border-zink-500 dark:bg-zink-800">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/customer/dashboard" className="flex shrink-0 items-center">
          <img src={logoDark} alt={appName} className="h-8 dark:hidden" />
          <img src={logoLight} alt={appName} className="hidden h-8 dark:block" />
        </Link>

        {/* Search - Desktop */}
        <form onSubmit={handleSearch} className="hidden flex-1 max-w-xl mx-4 md:block">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400 dark:text-zink-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full rounded-lg border border-slate-200 py-2 pl-10 pr-4 text-sm focus:border-custom-500 focus:outline-none focus:ring-1 focus:ring-custom-500 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
            />
          </div>
        </form>

        {/* Nav links - Desktop */}
        <nav className="hidden items-center gap-1 md:flex">
          {customerMenuData.map((item) => (
            <Link
              key={item.id}
              to={item.link}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-custom-500 dark:text-zink-200 dark:hover:bg-zink-600 dark:hover:text-custom-400"
            >
              {item.icon && React.cloneElement(item.icon, { className: 'size-4' })}
              <span className="hidden lg:inline">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <Link
            to="/customer/cart"
            className="relative flex items-center justify-center rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-custom-500 dark:text-zink-200 dark:hover:bg-zink-600"
          >
            <ShoppingCart className="size-5" />
          </Link>
          <Link
            to="/customer/wishlist"
            className="flex items-center justify-center rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-custom-500 dark:text-zink-200 dark:hover:bg-zink-600"
          >
            <Heart className="size-5" />
          </Link>
          <LightDark />
          <Dropdown className="relative">
            <Dropdown.Trigger
              type="button"
              className="flex items-center gap-2 rounded-full p-1 text-slate-600 hover:bg-slate-100 dark:text-zink-200 dark:hover:bg-zink-600"
            >
              <img src={userProfile} alt="" className="size-8 rounded-full" />
            </Dropdown.Trigger>
            <Dropdown.Content
              placement="right-end"
              className="absolute right-0 z-50 mt-2 min-w-[12rem] rounded-lg bg-white p-2 shadow-lg dark:bg-zink-600"
            >
              <div className="border-b border-slate-200 px-3 py-2 dark:border-zink-500">
                <p className="text-sm font-medium text-slate-700 dark:text-zink-100">
                  {user?.fullName || 'Customer'}
                </p>
                <p className="text-xs text-slate-500 dark:text-zink-400">{user?.email}</p>
              </div>
              <Link
                to="/customer/profile"
                className="flex items-center gap-2 rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-zink-200 dark:hover:bg-zink-500"
              >
                <User className="size-4" />
                Profile
              </Link>
              <Link
                to="/customer/orders"
                className="flex items-center gap-2 rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-zink-200 dark:hover:bg-zink-500"
              >
                <ShoppingCart className="size-4" />
                Orders
              </Link>
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
              >
                <LogOut className="size-4" />
                Sign Out
              </button>
            </Dropdown.Content>
          </Dropdown>
          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="rounded-lg p-2 md:hidden text-slate-600 hover:bg-slate-100 dark:text-zink-200 dark:hover:bg-zink-600"
          >
            <Menu className="size-5" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="border-t border-slate-200 px-4 py-3 md:hidden dark:border-zink-500">
          <form onSubmit={handleSearch} className="mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full rounded-lg border border-slate-200 py-2 pl-10 pr-4 text-sm dark:border-zink-500 dark:bg-zink-700"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-1">
            {customerMenuData.map((item) => (
              <Link
                key={item.id}
                to={item.link}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-zink-200 dark:hover:bg-zink-600"
              >
                {item.icon && React.cloneElement(item.icon, { className: 'size-4' })}
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default CustomerHeader;
